import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Sparkles, CheckCircle2, Loader2, Lightbulb, Send, AlertTriangle } from 'lucide-react';

/**
 * VoiceMeter — single-section editor that turns AI-drafted content into
 * the doctor's own voice. Shows a calm horizontal progress ring "Your voice:
 * 14% / 40%", inline +% suggestions, and unlocks the Publish button only at
 * the 40% threshold.
 *
 * Backend audit trail is kept in `doctors.voice_drafts.{section}` so we can
 * prove human-in-the-loop edits to Google manual-action reviewers.
 */
export default function VoiceMeter({ section = 'bio', heading = 'About Me', onPublished }) {
  const [loading, setLoading] = useState(true);
  const [aiOriginal, setAiOriginal] = useState('');
  const [text, setText] = useState('');
  const [score, setScore] = useState(0);
  const [threshold, setThreshold] = useState(40);
  const [hints, setHints] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [publishedAt, setPublishedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const debounceRef = useRef(null);

  // Load (or generate) the AI draft for this section
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post(`/dashboard/voice/draft/${section}`);
        if (cancelled) return;
        setAiOriginal(data.ai_original || '');
        setText(data.current || data.ai_original || '');
        setScore(data.voice_score || 0);
        setThreshold(data.threshold_pct || 40);
        setPublishedAt(data.published_at || null);
      } catch (e) {
        setErr(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [section]);

  // Score + suggestions are recomputed on every keystroke (debounced 600ms).
  const scoreNow = useCallback(async (current) => {
    try {
      const { data } = await api.post('/dashboard/voice/score', { section, current });
      setScore(data.voice_score || 0);
      setHints(data.suggestions || []);
    } catch (e) { /* silent — keystroke noise */ }
  }, [section]);

  useEffect(() => {
    if (loading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => scoreNow(text), 600);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [text, loading, scoreNow]);

  const publish = async () => {
    setPublishing(true); setErr('');
    try {
      const { data } = await api.post('/dashboard/voice/publish', { section, final_text: text });
      setPublishedAt(data.published_at);
      onPublished && onPublished(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setPublishing(false); }
  };

  const regen = async () => {
    if (!window.confirm("Regenerate the AI draft? Your current edits will be replaced.")) return;
    setBusy(true);
    try {
      // Force regenerate by clearing the audit record server-side, then re-fetch
      await api.post(`/dashboard/voice/publish`, { section, final_text: ' ' }).catch(() => {});
      const { data } = await api.post(`/dashboard/voice/draft/${section}`);
      setAiOriginal(data.ai_original || '');
      setText(data.ai_original || '');
      setScore(0);
      setPublishedAt(null);
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-2 text-sm text-slate-500" data-testid={`voice-meter-${section}-loading`}>
        <Loader2 size={16} className="animate-spin" /> Drafting your starting point…
      </div>
    );
  }

  // Color the ring: amber < 25, green-tint 25-39, full green ≥ threshold.
  const ringColor = score >= threshold ? '#10b981' : score >= 25 ? '#84cc16' : '#f59e0b';
  const reached = score >= threshold;
  const remaining = Math.max(0, threshold - score);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 space-y-4" data-testid={`voice-meter-${section}`}>
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-sky-100 flex items-center justify-center shrink-0">
          <Sparkles size={18} className="text-violet-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{heading}</h3>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
            We've drafted a starting point. Edit it until your <span className="font-semibold text-violet-700">"voice"</span> hits {threshold}% — then your section goes live on Google as authentically authored content.
          </p>
        </div>
        {/* Progress ring */}
        <div className="relative w-16 h-16 shrink-0" data-testid={`voice-ring-${section}`}>
          <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
            <circle cx="18" cy="18" r="15.915" fill="none" stroke="#f1f5f9" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.915" fill="none" stroke={ringColor} strokeWidth="3"
              strokeDasharray={`${Math.min(100, score)} 100`} strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 480ms cubic-bezier(0.34, 1.56, 0.64, 1), stroke 360ms ease-out' }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-base font-bold leading-none" style={{ color: ringColor }}>{score}<span className="text-xs">%</span></span>
            <span className="text-[8px] uppercase tracking-widest text-slate-400 mt-0.5">voice</span>
          </div>
        </div>
      </header>

      {/* Editor */}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={Math.max(8, Math.min(20, (text.match(/\n/g)?.length || 0) + 6))}
        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/40 text-sm leading-relaxed font-mono outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 resize-y"
        placeholder="Edit the AI draft above — make it sound like you talk to your patients."
        data-testid={`voice-textarea-${section}`}
      />

      {/* Live status row */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {reached ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold" data-testid={`voice-reached-${section}`}>
            <CheckCircle2 size={12} /> {score}% — ready to publish
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold" data-testid={`voice-progress-${section}`}>
            {remaining}% more to reach {threshold}%
          </span>
        )}
        {publishedAt && (
          <span className="text-emerald-600">Published {new Date(publishedAt).toLocaleDateString()}</span>
        )}
      </div>

      {/* Inline suggestions */}
      {!reached && hints && hints.length > 0 && (
        <div className="rounded-xl border border-violet-100 bg-violet-50/50 p-4" data-testid={`voice-hints-${section}`}>
          <p className="text-[10px] font-bold text-violet-700 uppercase tracking-widest mb-2 flex items-center gap-1">
            <Lightbulb size={11} /> Quick wins
          </p>
          <ul className="space-y-1.5">
            {hints.slice(0, 5).map(h => (
              <li key={h.id} className="flex items-start gap-2 text-xs text-slate-700" data-testid={`voice-hint-${h.id}`}>
                <span className="px-1.5 py-0.5 rounded-md bg-violet-100 text-violet-700 font-bold text-[10px] shrink-0 mt-0.5">+{h.delta_pct}%</span>
                <span className="leading-relaxed">{h.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {err && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700 flex items-center gap-1.5" data-testid={`voice-error-${section}`}>
          <AlertTriangle size={12} /> {err}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button onClick={publish} disabled={!reached || publishing}
          className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${reached ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-200 text-slate-500 cursor-not-allowed'} disabled:opacity-50`}
          data-testid={`voice-publish-${section}`}>
          {publishing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {publishing ? 'Publishing…' : (publishedAt ? 'Update & republish' : 'Publish to my page')}
        </button>
        <button onClick={regen} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full bg-white border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
          data-testid={`voice-regen-${section}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          Regenerate AI draft
        </button>
      </div>
    </section>
  );
}
